import { useState } from "react";
import {
  useGetMemberCharge, getGetMemberChargeQueryKey,
  useUpdateMemberCharge, getListMemberChargesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/auth-context";

interface Props {
  chargeId: number | null;
  onClose: () => void;
}

export function WaiveChargeDialog({ chargeId, onClose }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const { data: charge } = useGetMemberCharge(chargeId ?? 0, {
    query: { queryKey: getGetMemberChargeQueryKey(chargeId ?? 0), enabled: chargeId != null },
  });

  const mutation = useUpdateMemberCharge({
    mutation: {
      onSuccess: () => {
        toast.success("Charge waived");
        qc.invalidateQueries({ queryKey: getListMemberChargesQueryKey() });
        onClose();
      },
      onError: (err) => toast.error(`Waive failed: ${(err as Error).message}`),
    },
  });

  function submit() {
    if (!chargeId) return;
    if (reason.trim().length < 10) { toast.error("A reason is required to waive this charge (minimum 10 characters)"); return; }
    if (!confirmed) { toast.error("Confirm the waiver checkbox"); return; }
    // reason is audit-logged via description field; status set to Waived (D-29)
    mutation.mutate({
      id: chargeId,
      data: { status: "Waived", description: `[Waived] ${reason.trim()}` },
    });
  }

  if (!isAdmin) {
    return null; // Admin only — non-admin sees nothing (button is disabled in inbox)
  }

  return (
    <Dialog open={chargeId != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Waive Charge — Admin only</DialogTitle>
        </DialogHeader>
        <Alert className="border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle>Irreversible</AlertTitle>
          <AlertDescription>
            This action cannot be undone. The charge will be written off and audit-logged under your account (D-29).
          </AlertDescription>
        </Alert>
        {charge ? (
          <div className="text-sm space-y-1 mt-3 p-3 border rounded">
            <div>Member: MEM-{String(charge.memberId).padStart(4, "0")}</div>
            <div>Description: {charge.description}</div>
            <div>Amount: <span className="font-mono">Rs. {Number(charge.total).toFixed(2)}</span></div>
            <div>Date: {new Date(charge.transactionDate).toLocaleDateString("en-IN")}</div>
          </div>
        ) : null}
        <div className="space-y-2 mt-3">
          <Label htmlFor="waive-reason">Reason (required, ≥10 chars)</Label>
          <Textarea
            id="waive-reason"
            placeholder="Reason for waiver (required)"
            minLength={10}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
          />
          <div className="flex items-center gap-2">
            <Checkbox id="waive-confirm" checked={confirmed} onCheckedChange={(v) => setConfirmed(Boolean(v))} />
            <Label htmlFor="waive-confirm" className="text-xs">
              I confirm this waiver will be audit-logged under my account.
            </Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Waive Charge
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
